import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography
} from '@mui/material';
import { authorizedViewerUrl } from '../../services/viewerAccess';
import {
  finalizeExternalReferralReport,
  getExternalReferralCase,
  openExternalReferral,
  openReferralDocument,
  saveExternalReferralDraft,
  submitExternalReferralUrgentFinding
} from '../../services/externalReportingReferrals';

const EMPTY_DRAFT = {
  clinicianName: '',
  clinicalHistory: '',
  technique: '',
  comparison: '',
  findings: '',
  impression: '',
  recommendations: ''
};

function readInvitationToken() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = params.get('token') || '';
  if (token) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
  return token;
}

export default function ExternalReportingWorkspace() {
  const [invitationToken] = useState(readInvitationToken);
  const [clinicianName, setClinicianName] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [caseData, setCaseData] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [loading, setLoading] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [savingState, setSavingState] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [urgentText, setUrgentText] = useState('');
  const [urgentSending, setUrgentSending] = useState(false);

  const caseInfo = caseData?.case;
  const referral = caseData?.referral;
  const referralStatus = referral?.status;
  const viewerUrl = useMemo(() => {
    if (!caseInfo?.studyUID || !sessionToken) return '';
    try {
      return authorizedViewerUrl(caseInfo.studyUID, sessionToken);
    } catch (viewerError) {
      return '';
    }
  }, [caseInfo?.studyUID, sessionToken]);

  const refreshCase = useCallback(async (token = sessionToken) => {
    if (!token) return;
    const data = await getExternalReferralCase(token);
    setCaseData(previous => previous
      ? { ...previous, referral: { ...previous.referral, ...data.referral, draft: previous.referral.draft } }
      : data);
  }, [sessionToken]);

  const handleOpen = async () => {
    if (!invitationToken) return;
    setLoading(true);
    setError('');
    try {
      const data = await openExternalReferral(invitationToken, clinicianName);
      setSessionToken(data.sessionToken);
      setCaseData(data);
      setDraft({ ...EMPTY_DRAFT, ...(data.referral.draft || {}) });
      setSavingState('saved');
    } catch (openError) {
      setError(openError.response?.data?.error || 'Unable to open this invitation. Ask the referring clinic to send a new link.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    setError('');
    try {
      await openExternalReferral(invitationToken, clinicianName, 'decline');
      setDeclined(true);
    } catch (declineError) {
      setError(declineError.response?.data?.error || 'Unable to decline this invitation.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionToken || !['accepted', 'in_progress'].includes(referralStatus)) return undefined;
    const timer = window.setTimeout(async () => {
      setSavingState('saving');
      try {
        const result = await saveExternalReferralDraft(sessionToken, draft);
        setSavingState('saved');
        setCaseData(previous => previous
          ? { ...previous, referral: { ...previous.referral, status: result.status } }
          : previous);
      } catch (autosaveError) {
        console.error('External referral autosave failed:', autosaveError);
        setSavingState('error');
        setSaveError(autosaveError.response?.data?.error || 'Connection error. Your latest changes are not saved.');
      }
    }, 900);
    return () => window.clearTimeout(timer);
  }, [draft, sessionToken, referralStatus]);

  useEffect(() => {
    if (!sessionToken || !referral?.urgentFinding?.raisedAt || referral.urgentFinding.acknowledgedAt) return undefined;
    const timer = window.setInterval(() => {
      getExternalReferralCase(sessionToken).then(data => {
        setCaseData(previous => previous
          ? { ...previous, referral: { ...previous.referral, urgentFinding: data.referral.urgentFinding } }
          : previous);
      }).catch(refreshError => console.error('Urgent acknowledgment refresh failed:', refreshError));
    }, 15000);
    return () => window.clearInterval(timer);
  }, [sessionToken, referral?.urgentFinding?.raisedAt, referral?.urgentFinding?.acknowledgedAt]);

  const setField = (field, value) => setDraft(previous => ({ ...previous, [field]: value }));

  const handleUrgentFinding = async () => {
    setUrgentSending(true);
    setError('');
    try {
      await submitExternalReferralUrgentFinding(sessionToken, urgentText);
      setUrgentText('');
      await refreshCase();
    } catch (urgentError) {
      setError(urgentError.response?.data?.error || 'The urgent alert could not be sent. Please retry.');
    } finally {
      setUrgentSending(false);
    }
  };

  const handleFinalize = async () => {
    setSubmitting(true);
    setError('');
    try {
      const result = await finalizeExternalReferralReport(sessionToken, draft, clinicianName || draft.clinicianName);
      setReceipt(result.receipt);
      setSessionToken('');
      setCaseData(null);
      setConfirmOpen(false);
    } catch (submitError) {
      setError(submitError.response?.data?.error || 'Submission failed. Your draft is preserved; please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  if (receipt) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 5 } }}>
        <Card><CardContent>
          <Typography variant="h4" gutterBottom>Report submitted</Typography>
          <Alert severity="success" sx={{ mb: 2 }}>Your report was saved successfully. Access to the referred case has ended.</Alert>
          <Typography><strong>Referral reference:</strong> {receipt.referralReference}</Typography>
          <Typography><strong>Submitted:</strong> {new Date(receipt.submittedAt).toLocaleString()}</Typography>
          <Typography sx={{ mt: 2 }}>Keep this receipt for your records. No patient information is available from this page.</Typography>
        </CardContent></Card>
      </Box>
    );
  }

  if (!sessionToken || !caseData) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, md: 5 } }}>
        <Card><CardContent>
          {declined ? (
            <Alert severity="info">You declined this referral. Your response was recorded and the referring clinic can see the referral status.</Alert>
          ) : (
          <>
          <Typography variant="h4" gutterBottom>External reporting referral</Typography>
          <Typography sx={{ mb: 2 }}>
            This confidential invitation lets you review one imaging examination and submit a report. You do not need a DycoVue account or password.
          </Typography>
          {!invitationToken && <Alert severity="warning" sx={{ mb: 2 }}>Invitation link is missing. Request a new link from the referring clinic.</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth required label="Your name as it should appear on the report"
            value={clinicianName} onChange={event => setClinicianName(event.target.value)}
            sx={{ mb: 2 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" disabled={!invitationToken || loading || !clinicianName.trim()} onClick={handleOpen}>
              {loading ? <CircularProgress size={20} /> : 'Accept and open case'}
            </Button>
            <Button color="inherit" disabled={!invitationToken || loading} onClick={handleDecline}>Decline invitation</Button>
          </Stack>
          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            The referral is not accepted by opening the email or this page. Acceptance occurs only when you select “Accept and open case”.
          </Typography>
          </>
          )}
        </CardContent></Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4">External report</Typography>
          <Typography color="text.secondary">
            {caseInfo.patientName} · {caseInfo.patientId} · {caseInfo.modality} · {caseInfo.studyDescription || 'Imaging examination'}
          </Typography>
          <Typography variant="caption">
            {caseInfo.studyDate ? new Date(caseInfo.studyDate).toLocaleDateString() : ''} · Accession {caseInfo.accessionNumber || 'not provided'}
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        <Alert severity={savingState === 'error' ? 'error' : 'info'}>
          {savingState === 'saving' ? 'Saving draft…' :
            savingState === 'saved' ? 'Draft saved' :
              savingState === 'error' ? saveError :
                'Draft autosave is ready.'}
        </Alert>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Images</Typography>
          {viewerUrl
            ? <Box component="iframe" title="Referred examination images" src={viewerUrl}
                sx={{ width: '100%', height: { xs: 480, md: 700 }, border: 0, borderRadius: 1 }} />
            : <Alert severity="warning">The image viewer is not configured for this site.</Alert>}
        </CardContent></Card>
        {(referral.sharedNotes || referral.amendmentReason) && (
          <Alert severity="info">
            {referral.sharedNotes && <Typography><strong>Shared clinical notes:</strong> {referral.sharedNotes}</Typography>}
            {referral.amendmentReason && <Typography><strong>Amendment request:</strong> {referral.amendmentReason}</Typography>}
          </Alert>
        )}
        {caseData.sharedDocuments?.length > 0 && (
          <Card><CardContent>
            <Typography variant="h6" gutterBottom>Documents shared for this referral</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {caseData.sharedDocuments.map(id => (
                <Button key={id} onClick={() => openReferralDocument(sessionToken, id).catch(documentError => {
                  console.error('Unable to open shared document:', documentError);
                  setError('The shared document could not be opened.');
                })}>Open shared report</Button>
              ))}
            </Stack>
          </CardContent></Card>
        )}
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Report workspace</Typography>
          <Stack spacing={2}>
            <TextField label="Reporting clinician" value={draft.clinicianName || clinicianName}
              onChange={event => { setClinicianName(event.target.value); setField('clinicianName', event.target.value); }} />
            <TextField label="Clinical history" multiline minRows={2} value={draft.clinicalHistory} onChange={event => setField('clinicalHistory', event.target.value)} />
            <TextField label="Technique" multiline minRows={2} value={draft.technique} onChange={event => setField('technique', event.target.value)} />
            <TextField label="Comparison" multiline minRows={2} value={draft.comparison} onChange={event => setField('comparison', event.target.value)} />
            <TextField label="Findings" required multiline minRows={5} value={draft.findings} onChange={event => setField('findings', event.target.value)} />
            <TextField label="Impression" required multiline minRows={3} value={draft.impression} onChange={event => setField('impression', event.target.value)} />
            <TextField label="Recommendations" multiline minRows={2} value={draft.recommendations} onChange={event => setField('recommendations', event.target.value)} />
          </Stack>
        </CardContent></Card>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Urgent finding</Typography>
          {referral.urgentFinding?.raisedAt ? (
            <Alert severity="warning">
              Alert sent {new Date(referral.urgentFinding.raisedAt).toLocaleString()}.
              {referral.urgentFinding.acknowledgedAt
                ? ` Acknowledged ${new Date(referral.urgentFinding.acknowledgedAt).toLocaleString()}.`
                : ' Awaiting acknowledgment from the referring clinic.'}
            </Alert>
          ) : (
            <Stack spacing={1}>
              <TextField label="Urgent finding to alert the referring clinic" multiline minRows={2}
                inputProps={{ maxLength: 2000 }} value={urgentText} onChange={event => setUrgentText(event.target.value)} />
              <Button color="warning" variant="outlined" disabled={urgentSending || !urgentText.trim()} onClick={handleUrgentFinding}>
                {urgentSending ? 'Sending alert…' : 'Send urgent finding'}
              </Button>
            </Stack>
          )}
        </CardContent></Card>
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" onClick={() => setConfirmOpen(true)}>Review and finalize</Button>
        </Stack>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Confirm final report submission</DialogTitle>
        <DialogContent dividers>
          <Typography><strong>Patient:</strong> {caseInfo.patientName} ({caseInfo.patientId})</Typography>
          <Typography><strong>Examination:</strong> {caseInfo.modality} — {caseInfo.studyDescription || 'Imaging examination'} · {caseInfo.accessionNumber || 'No accession number'} · {caseInfo.studyDate ? new Date(caseInfo.studyDate).toLocaleDateString() : 'Date not provided'}</Typography>
          <Typography><strong>Reporting clinician:</strong> {clinicianName || draft.clinicianName || 'Not provided'}</Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>Findings</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{draft.findings}</Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>Clinical history</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{draft.clinicalHistory}</Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>Technique</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{draft.technique}</Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>Comparison</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{draft.comparison}</Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>Impression</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{draft.impression}</Typography>
          {draft.recommendations && <><Typography variant="h6" sx={{ mt: 2 }}>Recommendations</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{draft.recommendations}</Typography></>}
          <Alert severity="warning" sx={{ mt: 2 }}>Final submission locks this report and ends your access to this case.</Alert>
        </DialogContent>
        <DialogActions>
          <Button disabled={submitting} onClick={() => setConfirmOpen(false)}>Continue editing</Button>
          <Button disabled={submitting || !(draft.findings.trim() || draft.impression.trim())}
            variant="contained" onClick={handleFinalize}>
            {submitting ? 'Submitting…' : 'Finalize and submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
